import React, { useEffect, useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, Globe, MapPin } from "lucide-react";

const TIER_FEATURES = {
  starter:    ["1 project", "5 team members", "50 AI scans / mo", "Email support"],
  pro:        ["10 projects", "50 team members", "500 AI scans / mo", "Voice logs + QR", "Priority support"],
  enterprise: ["Unlimited projects", "Unlimited team", "Unlimited AI", "SSO + Audit", "Dedicated CSM"],
};

// USD pricing (Razorpay International). Roughly INR ÷ 83.
const USD_PLANS = {
  starter:    { monthly: 12,  yearly: 120 },
  pro:        { monthly: 29,  yearly: 290 },
  enterprise: { monthly: 99,  yearly: 990 },
};

export default function Subscription() {
  const { t } = useLang();
  const { user, refreshUser } = useAuth();
  const [currency, setCurrency] = useState("INR"); // "INR" or "USD"
  const [cycle, setCycle] = useState("monthly");
  const [plansINR, setPlansINR] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient().get("/billing/plans").then((r) => setPlansINR(r.data)).catch(() => {});
  }, []);

  const tiers = ["starter", "pro", "enterprise"];

  const subscribe = async (planKey) => {
    setBusy(true);
    try {
      const { data } = await apiClient().post("/billing/subscribe", { plan: planKey, cycle, currency });
      // Demo flow: verify immediately
      if (data.demo) {
        await apiClient().post("/billing/verify", {
          demo: true, plan: planKey, cycle, currency,
          razorpay_order_id: data.order_id, razorpay_payment_id: "demo_pay", razorpay_signature: "demo_sig",
        });
        toast.success(`Activated ${planKey.toUpperCase()} · ${cycle} (demo mode)`);
        await refreshUser();
        return;
      }
      // Real Razorpay Checkout (supports INR + international currencies)
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "Setubuild.OS",
        description: `${planKey.toUpperCase()} · ${cycle}`,
        prefill: { email: user?.email, name: user?.name },
        theme: { color: "#F5A524" },
        handler: async (r) => {
          await apiClient().post("/billing/verify", { ...r, plan: planKey, cycle, currency });
          toast.success("Payment verified — subscription activated!");
          await refreshUser();
        },
        modal: {
          ondismiss: () => toast.info("Payment cancelled"),
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed");
    } finally { setBusy(false); }
  };

  if (!plansINR) return <div className="text-muted-foreground">Loading pricing…</div>;

  const symbol = currency === "USD" ? "$" : "₹";

  return (
    <div className="space-y-6 fade-in-up" data-testid="subscription-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-1 tracking-tight">{t("choose_plan")}</h1>
        {user?.subscription?.status === "active" && (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <div className="mono text-xs text-emerald-500 inline-flex items-center gap-1" data-testid="active-subscription">
              <CheckCircle2 className="h-3.5 w-3.5" /> Active: {user.subscription.plan?.toUpperCase()} · {user.subscription.cycle}
            </div>
            {user.subscription.razorpay_payment_id && (
              <RefundButton onRefunded={refreshUser} />
            )}
          </div>
        )}
        {user?.subscription?.status === "refunded" && (
          <div className="mono text-xs text-amber-500 mt-2 inline-flex items-center gap-1" data-testid="refunded-subscription">
            Subscription refunded. You may subscribe again anytime.
          </div>
        )}
      </div>

      {/* Currency + Cycle toggles */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex border border-border overflow-hidden" data-testid="currency-toggle">
          <button onClick={() => setCurrency("INR")} data-testid="currency-inr"
            className={`px-4 py-2 text-sm mono uppercase tracking-widest inline-flex items-center gap-2 ${currency === "INR" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
            <MapPin className="h-3.5 w-3.5" /> India · INR
          </button>
          <button onClick={() => setCurrency("USD")} data-testid="currency-usd"
            className={`px-4 py-2 text-sm mono uppercase tracking-widest inline-flex items-center gap-2 ${currency === "USD" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
            <Globe className="h-3.5 w-3.5" /> Global · USD
          </button>
        </div>
        <div className="inline-flex border border-border overflow-hidden" data-testid="cycle-toggle">
          {["monthly", "yearly"].map((c) => (
            <button key={c} onClick={() => setCycle(c)} data-testid={`cycle-${c}`}
              className={`px-4 py-2 text-sm mono uppercase tracking-widest ${cycle === c ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              {t(c)}
            </button>
          ))}
        </div>
      </div>

      {plansINR?.demo_mode && null}

      <div className="grid md:grid-cols-3 gap-4">
        {tiers.map((key) => {
          const highlight = key === "pro";
          const features = TIER_FEATURES[key];
          const price = currency === "USD"
            ? USD_PLANS[key]?.[cycle]
            : plansINR?.plans?.[key]?.[cycle];
          const priceLabel = price ? `${symbol}${currency === "INR" ? price.toLocaleString() : price}` : "—";
          return (
            <div key={key}
              data-testid={`sub-plan-${key}`}
              className={`border p-6 flex flex-col ${highlight ? "border-primary ring-1 ring-primary/40 bg-card" : "border-border bg-card"}`}>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
                {highlight ? <span className="inline-flex items-center gap-1 text-primary"><Sparkles className="h-3 w-3" /> Popular</span> : <span>Plan</span>}
                <span>{cycle}</span>
              </div>
              <div className="font-heading text-4xl mt-1 uppercase tracking-tight">{key}</div>
              <div className="mt-3">
                <span className="font-heading text-4xl">{priceLabel}</span>
                <span className="text-muted-foreground text-sm"> /{cycle === "monthly" ? "mo" : "yr"}</span>
              </div>
              <ul className="mt-6 space-y-2 text-sm flex-1">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" /><span>{f}</span></li>
                ))}
              </ul>
              <button onClick={() => subscribe(key)} disabled={busy} data-testid={`sub-btn-${key}`}
                className="btn-sharp mt-6 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-3 font-medium disabled:opacity-50">
                {busy ? "…" : t("subscribe_now")}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground pt-4">
        Payments powered by Razorpay · UPI, Cards, Netbanking (India) · International Cards (Global) · GST invoice on demand · <a href="/refund" className="hover:text-primary underline">Refund policy</a>
      </div>
    </div>
  );
}

const RefundButton = ({ onRefunded }) => {
  const [busy, setBusy] = React.useState(false);
  const handleRefund = async () => {
    const reason = window.prompt("We're sorry to see you go. Tell us the reason (optional):", "");
    if (reason === null) return; // cancelled
    if (!window.confirm("Confirm refund? Your subscription will be cancelled and amount refunded to source in 5-7 business days.")) return;
    setBusy(true);
    try {
      const { data } = await apiClient().post("/billing/refund", { reason });
      if (data.auto_refunded) {
        toast.success("Refund processed. Check your account in 5-7 days.");
      } else {
        toast.info(data.message || "Refund request received. Team will review.");
      }
      await onRefunded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund request failed");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={handleRefund} disabled={busy} data-testid="request-refund-btn"
      className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-border rounded hover:border-red-500/50 hover:text-red-500 transition disabled:opacity-50">
      {busy ? "Processing…" : "Request refund"}
    </button>
  );
};
